import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useNotification } from 'config/Notification';
import { deleteUserService, getUserService } from 'services/modules/idp/user';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone, faUser, faCheckCircle, faUsers, faTag } from '@fortawesome/free-solid-svg-icons';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import {
  Button,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider
} from '@mui/material';
import AccountBoxIcon from '@mui/icons-material/AccountBox';

const Profile = () => {
  // State variables
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userNameToDelete, setUserNameToDelete] = useState(null);
  const [userRoleToDelete, setUserRoleDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  // Redux selectors
  const authenticatedUserContainer = useSelector((state) => state.auth.user);

  // React Router
  const navigate = useNavigate();

  // Notification
  const { openNotification } = useNotification();

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Method to fetch the user data based on user id
  const fetchUserData = async () => {
    try {
      setLoading(true);
      const path = window.location.pathname;
      const userId = path.split('/').pop();

      const response = await getUserService(userId);
      setUserData(response);
      setLoading(false);
    } catch (error) {
      openNotification('error fetching data:', error);
      setLoading(false);
    }
  };

  // Handles navigation to edit company component
  const handleEditUser = () => {
    navigate(`/update-profile/${userData.id}`);
  };

  // Method deletes the user based on user id
  const handleDeleteUser = async () => {
    try {
      const path = window.location.pathname;
      const userId = path.split('/').pop();
      await deleteUserService(userId);
      setDeleteDialogOpen(false);
      openNotification('User deleted successfully', 'success');
      window.history.back();
    } catch (error) {
      openNotification('Error deleting user', 'error');
    }
  };

  // Method sets the user details and opens the delete dialog
  const handleDeleteDialogOpen = () => {
    if (userData) {
      setUserNameToDelete(userData.name);
      setUserRoleDelete(userData.roles && userData.roles.map(role => role.label));
      setDeleteDialogOpen(true);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', background: '#f9f9f9', height: '800px' }}>
      <Card style={{ maxWidth: '800px', width: '100%', padding: '20px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <CardHeader
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img src="/assets/userprofile.75163155.svg" alt="User Icon" style={{ height: '40px', marginRight: '10px' }} />
                <Typography variant="h5" style={{ fontWeight: 'bold' }}>User Profile</Typography>
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.history.back()}
                className="back font"
              >
                Back
              </Button>
            </div>
          }
        />
        <Divider />
        <CardContent>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </div>
          ) : userData ? (
            <Grid container spacing={3}>
              {/* Profile Picture */}
              <Grid item xs={12} sm={4} style={{ textAlign: 'center' }}>
                <AccountBoxIcon style={{ width: '120px', height: '120px', margin: '0 auto' }} />
                <Typography variant="h6" style={{ marginTop: '10px', fontWeight: 'bold' }}>{userData.name}</Typography>
                <Typography variant="body2" color="textSecondary">{userData.username}</Typography>
              </Grid>

              {/* Profile Information */}
              <Grid item xs={12} sm={8}>
                <Grid container spacing={2}>
                  {/* Displaying user details */}
                  <Grid item xs={4}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                      <FontAwesomeIcon icon={faUser} /> Name:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">{userData.name}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                      <FontAwesomeIcon icon={faTag} /> Username:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">{userData.username}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                      <FontAwesomeIcon icon={faEnvelope} /> Email:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">{userData.email}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                      <FontAwesomeIcon icon={faUsers} /> Role:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">
                      {userData.roles.map((role, index) => (
                        <span key={index}>
                          {role.label}
                          {index < userData.roles.length - 1 && ', '}
                        </span>
                      ))}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                      <FontAwesomeIcon icon={faPhone} /> Mobile:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">
                      {userData.mobile && `${userData.mobile.code} ${userData.mobile.digits}`}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                      <FontAwesomeIcon icon={faCheckCircle} /> Status:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1" style={{ color: userData?.status === 'ACTIVE' ? 'green' : 'red' }}>
                      {userData?.status?.substring(0, 1).concat(userData?.status?.slice(1).toLowerCase())}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Typography variant="body1" color="textSecondary">No user data available.</Typography>
            </div>
          )}

          {/* Add buttons for Edit and Delete */}
          {authenticatedUserContainer &&
            authenticatedUserContainer.roles &&
            authenticatedUserContainer.roles.some((role) =>
              role.permissions.includes('UPDATE_CHILD_USERS')
            ) && (
              <div className="button-container">
                <Button variant="contained" className="btn-card" onClick={handleEditUser}>
                  <ModeEditIcon /> Edit user
                </Button>
                <Button variant="contained" className="btn-card danger" onClick={handleDeleteDialogOpen}>
                  <DeleteIcon /> Delete user
                </Button>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); }}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle className="dailogBox" id="delete-dialog-title">
          <h3 className="dailog-headin">Do you want to delete user?</h3>
          <CloseIcon className="close-Icon" onClick={() => { setDeleteDialogOpen(false); }} />
        </DialogTitle>
        <Divider className="divider" />
        <DialogContent>
          <div className='dailogBox'>
            <h4 className="dailog-text">{userNameToDelete}</h4>
            <ArrowRightIcon />
            <h4>{userRoleToDelete}</h4>
          </div>
        </DialogContent>
        <DialogActions className='m-3'>
          <Button onClick={() => { setDeleteDialogOpen(false); }} variant="contained" className="btn-card">
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} variant="contained" className="btn-card danger">
            <DeleteIcon /> Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Profile;
